import React, { useMemo, useState } from 'react';
import { subjectNamesFromStandards } from '../../utils/evaluationPlanDocTitle';
import type {
  EvaluationLevelDisplayLabels,
  EvaluationLevelDisplayLabelsByStepCount,
  EvaluationLevelStepCount,
  EvaluationPlanDraft,
  EvaluationPlanRowScope,
  EvaluationStandardDraft,
} from '../../types/evaluationPlan';
import {
  DEFAULT_LEVEL_STEP_COUNT,
  EVALUATION_PLAN_ATTACHMENT_SLOT_LABELS,
  clampLevelStepCount,
  comparePlansByLinkedStandards,
  compareStandardsByCode,
  formatPlanListSummary,
  formatStandardSummary,
  getEvaluationPlanAttachment,
  getLevelDisplayLabelsForStep,
  hasEvaluationPlanSlotAttachment,
  type EvaluationPlanAttachmentSlot,
} from '../../types/evaluationPlan';
import { isShareLinkAttachment, isSheetCanvasAttachment, isWebDocumentAttachment } from '../../utils/evaluationPlanAttachment';
import EvaluationPlanAttachmentPreviewModal from './EvaluationPlanAttachmentPreviewModal';
import EvaluationPlanShareLinkActionPopover from './EvaluationPlanShareLinkActionPopover';
import EvaluationPlanWebDocumentViewerModal from './EvaluationPlanWebDocumentViewerModal';
import EvaluationPlanSheetCanvasViewerModal from './EvaluationPlanSheetCanvasViewerModal';

function getPlanLinkedStandards(
  plan: EvaluationPlanDraft,
  standards: EvaluationStandardDraft[]
): EvaluationStandardDraft[] {
  const byId = new Map(standards.map((row) => [row.id, row]));
  return (plan.standard_ids ?? [])
    .map((id) => byId.get(id))
    .filter((row): row is EvaluationStandardDraft => !!row)
    .sort(compareStandardsByCode);
}

const EvaluationPlanAppliedListPanel: React.FC<{
  /** 현재 선택 클래스 표시명(예: 0000) */
  classLabel?: string | null;
  /** 클래스에 연결된 학년 숫자(원형 칩) */
  classGradeNumbers?: number[] | null;
  standards: EvaluationStandardDraft[];
  plans: EvaluationPlanDraft[];
  planScopeById?: Record<string, EvaluationPlanRowScope>;
  categoryNameByPlanId?: Record<string, string>;
  levelStepCount?: EvaluationLevelStepCount;
  levelDisplayLabels?: EvaluationLevelDisplayLabels | null;
  levelLabelsByStep?: EvaluationLevelDisplayLabelsByStepCount | null;
  onEditApplied: (plan: EvaluationPlanDraft) => void;
  onDeleteApplied: (planId: string) => void;
}> = ({
  classLabel,
  classGradeNumbers,
  standards,
  plans,
  planScopeById,
  categoryNameByPlanId,
  levelStepCount,
  levelDisplayLabels,
  levelLabelsByStep,
  onEditApplied,
  onDeleteApplied,
}) => {
  const sortedPlans = useMemo(
    () => [...plans].sort((a, b) => comparePlansByLinkedStandards(a, b, standards)),
    [plans, standards]
  );
  const [previewTarget, setPreviewTarget] = useState<{
    slot: EvaluationPlanAttachmentSlot;
    attachment: NonNullable<ReturnType<typeof getEvaluationPlanAttachment>>;
  } | null>(null);
  const [webDocumentViewerTarget, setWebDocumentViewerTarget] = useState<{
    subjectNames?: string[];
    slot: EvaluationPlanAttachmentSlot;
    attachment: NonNullable<ReturnType<typeof getEvaluationPlanAttachment>>;
    levelStepCount: EvaluationLevelStepCount;
    levelDisplayLabels: EvaluationLevelDisplayLabels | null;
  } | null>(null);
  const [sheetCanvasViewerTarget, setSheetCanvasViewerTarget] = useState<{
    subjectNames?: string[];
    attachment: NonNullable<ReturnType<typeof getEvaluationPlanAttachment>>;
  } | null>(null);

  const resolvePlanLevelMeta = (plan: EvaluationPlanDraft) => {
    const step = clampLevelStepCount(plan.level_step_count ?? levelStepCount ?? DEFAULT_LEVEL_STEP_COUNT);
    const labels =
      (levelLabelsByStep
        ? getLevelDisplayLabelsForStep(levelLabelsByStep, step)
        : levelDisplayLabels) ?? null;
    return { levelStepCount: step, levelDisplayLabels: labels };
  };

  const openAttachmentPreview = (plan: EvaluationPlanDraft, slot: EvaluationPlanAttachmentSlot) => {
    const attachment = getEvaluationPlanAttachment(plan, slot);
    if (!attachment || isShareLinkAttachment(attachment)) return;
    if (isWebDocumentAttachment(attachment)) {
      if (slot === 'sheet' && isSheetCanvasAttachment(attachment)) {
        setSheetCanvasViewerTarget({ attachment, subjectNames: subjectNamesFromStandards(getPlanLinkedStandards(plan, standards)) });
        return;
      }
      setWebDocumentViewerTarget({ slot, attachment, ...resolvePlanLevelMeta(plan), subjectNames: subjectNamesFromStandards(getPlanLinkedStandards(plan, standards)) });
      return;
    }
    setPreviewTarget({ slot, attachment });
  };

  const openWebDocumentViewer = (plan: EvaluationPlanDraft, slot: EvaluationPlanAttachmentSlot) => {
    const attachment = getEvaluationPlanAttachment(plan, slot);
    if (!attachment || !isWebDocumentAttachment(attachment)) return;
    if (slot === 'sheet' && isSheetCanvasAttachment(attachment)) {
      setSheetCanvasViewerTarget({ attachment, subjectNames: subjectNamesFromStandards(getPlanLinkedStandards(plan, standards)) });
      return;
    }
    setWebDocumentViewerTarget({ slot, attachment, ...resolvePlanLevelMeta(plan), subjectNames: subjectNamesFromStandards(getPlanLinkedStandards(plan, standards)) });
  };

  const renderAttachmentChipSlot = (plan: EvaluationPlanDraft, slot: EvaluationPlanAttachmentSlot) => {
    const label = EVALUATION_PLAN_ATTACHMENT_SLOT_LABELS[slot];
    const hasAttachment = hasEvaluationPlanSlotAttachment(plan, slot);
    if (!hasAttachment) {
      return (
        <span
          className={`cp-evaluation-plan-plan-attachment-chip-placeholder cp-evaluation-plan-plan-attachment-chip-placeholder--${slot}`}
          aria-hidden
        >
          {label}
        </span>
      );
    }
    const attachment = getEvaluationPlanAttachment(plan, slot);
    return (
      <EvaluationPlanShareLinkActionPopover
        attachment={attachment}
        onOpenDocument={
          attachment && isWebDocumentAttachment(attachment) ? () => openWebDocumentViewer(plan, slot) : undefined
        }
        onOpenPreview={
          attachment && !isShareLinkAttachment(attachment) && !isWebDocumentAttachment(attachment)
            ? () => openAttachmentPreview(plan, slot)
            : undefined
        }
        className="cp-evaluation-plan-plan-attachment-chip-popover-anchor"
      >
        <button type="button" className="cp-evaluation-plan-plan-attachment-chip">
          {label}
        </button>
      </EvaluationPlanShareLinkActionPopover>
    );
  };

  return (
    <article
      className="cp-dashboard-block cp-evaluation-plan-applied-block"
      aria-labelledby="cp-evaluation-plan-applied-head"
      data-tutorial-ep="applied-block"
    >
      <h3 className="cp-dashboard-block__head cp-evaluation-plan-section-head" id="cp-evaluation-plan-applied-head">
        <span className="cp-evaluation-plan-section-head-title">
          {(classLabel ?? '').trim() ? (
            <span className="cp-evaluation-plan-section-head-class-badge">
              <span className="cp-evaluation-plan-section-head-class-name">{(classLabel ?? '').trim()}</span>
              {(classGradeNumbers ?? []).length > 0 ? (
                <span className="cp-evaluation-plan-section-head-class-chips" aria-hidden>
                  {(classGradeNumbers ?? []).map((num) => (
                    <span key={num} className="class-grade-chip-display">
                      {num}
                    </span>
                  ))}
                </span>
              ) : null}
            </span>
          ) : null}
          클래스 평가계획 등록
        </span>
      </h3>
      <div className="cp-dashboard-block__body cp-evaluation-plan-section-body cp-evaluation-plan-applied-body">
        {sortedPlans.length === 0 ? (
          <p className="cp-dashboard-placeholder" role="status">
            평가계획 목록에서 항목을 선택한 뒤 → 버튼으로 적용해 주세요. 저장 시 이 클래스 전용 복사본으로 등록됩니다.
          </p>
        ) : (
          <ul className="cp-admin-notices-edu-list cp-evaluation-plan-section-list" role="list">
            {sortedPlans.map((plan) => {
              const linkedStandards = getPlanLinkedStandards(plan, standards);
              const planScope = planScopeById?.[plan.id] ?? 'user';
              const categoryLabel = categoryNameByPlanId?.[plan.id]?.trim() || '';
              return (
                <li key={plan.id} className="cp-admin-notices-edu-card" role="none">
                  <div className="cp-admin-notices-edu-card-inner cp-evaluation-plan-applied-card-inner">
                    <span
                      className={`cp-evaluation-plan-plan-scope-stripe cp-evaluation-plan-plan-scope-stripe--${planScope}`}
                      aria-hidden
                    />
                    <button
                      type="button"
                      className="cp-admin-notices-edu-card-main"
                      onClick={() => onEditApplied(plan)}
                    >
                      {categoryLabel ? (
                        <span className="cp-evaluation-plan-applied-category-label">{categoryLabel}</span>
                      ) : null}
                      <div className="cp-admin-notices-edu-card-meta">
                        {linkedStandards.length > 0 ? (
                          linkedStandards.map((standard) => (
                            <span
                              key={`${plan.id}-${standard.id}`}
                              className="cp-evaluation-plan-plan-linked-standard-line"
                            >
                              {formatStandardSummary(standard)}
                            </span>
                          ))
                        ) : (
                          <span className="cp-evaluation-plan-plan-linked-standard-line">(연결 성취기준 없음)</span>
                        )}
                      </div>
                      <span className="cp-admin-notices-edu-card-title">{formatPlanListSummary(plan)}</span>
                    </button>
                    <div className="cp-admin-notices-edu-card-tools">
                      <div
                        className="cp-evaluation-plan-plan-attachment-chips"
                        role="group"
                        aria-label="등록된 문서"
                      >
                        <div className="cp-evaluation-plan-plan-attachment-chip-slot cp-evaluation-plan-plan-attachment-chip-slot--criterion">
                          {renderAttachmentChipSlot(plan, 'criterion')}
                        </div>
                        <div className="cp-evaluation-plan-plan-attachment-chip-slot cp-evaluation-plan-plan-attachment-chip-slot--sheet">
                          {renderAttachmentChipSlot(plan, 'sheet')}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="cp-admin-notices-icon-btn"
                        title="적용 평가계획 수정"
                        aria-label="적용 평가계획 수정"
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditApplied(plan);
                        }}
                      >
                        <img
                          src={`${process.env.PUBLIC_URL}/edit.svg`}
                          alt=""
                          className="cp-admin-notices-tool-icon"
                          width={18}
                          height={18}
                          decoding="async"
                        />
                      </button>
                      <button
                        type="button"
                        className="cp-admin-notices-icon-btn"
                        title="적용 평가계획 삭제"
                        aria-label="적용 평가계획 삭제"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteApplied(plan.id);
                        }}
                      >
                        <img
                          src={`${process.env.PUBLIC_URL}/Trash.svg`}
                          alt=""
                          className="cp-admin-notices-tool-icon"
                          width={18}
                          height={18}
                          decoding="async"
                        />
                      </button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <EvaluationPlanAttachmentPreviewModal
        open={previewTarget != null}
        slot={previewTarget?.slot ?? null}
        attachment={previewTarget?.attachment ?? null}
        onClose={() => setPreviewTarget(null)}
      />
      <EvaluationPlanWebDocumentViewerModal
        open={webDocumentViewerTarget != null}
        slot={webDocumentViewerTarget?.slot ?? null}
        attachment={webDocumentViewerTarget?.attachment ?? null}
        levelStepCount={webDocumentViewerTarget?.levelStepCount ?? levelStepCount}
        levelDisplayLabels={webDocumentViewerTarget?.levelDisplayLabels ?? levelDisplayLabels}
        subjectNames={webDocumentViewerTarget?.subjectNames}
        onClose={() => setWebDocumentViewerTarget(null)}
      />
      <EvaluationPlanSheetCanvasViewerModal
        open={sheetCanvasViewerTarget != null}
        slot="sheet"
        attachment={sheetCanvasViewerTarget?.attachment ?? null}
        subjectNames={sheetCanvasViewerTarget?.subjectNames}
        onClose={() => setSheetCanvasViewerTarget(null)}
      />
    </article>
  );
};

export default EvaluationPlanAppliedListPanel;
